/**
 * `<DiceFeedSubscriber>` — the dice side's consumer of chat's authoritative game feed. It
 * renders nothing; it watches the `/ws/game` feed and, when a **new ad-hoc `/roll`** lands,
 * tumbles it on the global {@link ./DiceOverlay} via {@link throwFeedRoll}. Mounted once as a
 * **peer** of `<DiceOverlay>`/`<DirectiveOverlay>` in `GameSessionProvider` — a sibling of the
 * route tree, not inside the window manager — so a `/roll` from the desktop OR the combat
 * surface tumbles the same overlay.
 *
 * **This is the whole "subscribe the overlay to the existing feed" wiring.** Chat owns the
 * authoritative `/roll` command + RNG + the durable {@link RollFeedEntry}; this never re-rolls or
 * builds a second command — it only visualizes the record. Remove this one component and
 * COMMS.app + the feed are fully functional (the dice module's isolation invariant).
 *
 * Two correctness rules, both load-bearing:
 *
 *  1. **Dedup vs. combat resolutions.** The feed *also* carries combat-resolution events, but as
 *     `kind: "system"` (`event: "resolution"`) — and those are already visualized through the
 *     *encounter* path (`CombatApp` → `throwSteps` off `encounter.latestResolution`). So we throw
 *     **only `kind: "roll"`** entries (the ad-hoc `/roll` cards); system/chat entries are ignored.
 *     That keeps an ad-hoc roll tumbling exactly once and never double-visualizes a combat roll.
 *
 *  2. **No first-mount / reload backlog flurry.** The feed seeds from a bounded historical tail
 *     on (re)connect (`game_welcome.feed`, up to {@link FEED_TAIL_CAP}), delivered ASYNCHRONOUSLY
 *     after mount — `useGameSession`'s `feed` starts `[]`. Seeding an "already seen" baseline off
 *     the subscriber's own first observation is therefore wrong: that first pass sees the empty
 *     array, records an empty baseline, and then the backfill lands and reads as entirely new —
 *     replaying the most recent roll on every page load. Instead, freshness is judged against
 *     `feedBaselineTs` — the max `ts` of the welcome backfill itself
 *     (`GameSession.feedBaselineTs`, re-seeded on every `game_welcome`, including a reconnect) —
 *     via the pure {@link selectFreshRolls}. Only entries with `ts > highWater` throw; a
 *     mid-session reconnect that re-delivers the tail raises the high-water mark right back up
 *     and throws nothing.
 */
import { useEffect, useRef } from "react";
import type { FeedEntry } from "@lancer-vtt/shared";
import { throwFeedRoll } from "./diceController";
import { selectFreshRolls } from "./feedFreshness";
import { registerResolutionThrow } from "./resolutionThrows";

export function DiceFeedSubscriber({ feed, feedBaselineTs }: { feed: readonly FeedEntry[]; feedBaselineTs: number }) {
  // The freshness high-water mark. Seeded from `feedBaselineTs` (the welcome backfill's own max
  // `ts`) rather than anything observed at mount — see rule 2 above. A ref because it must
  // survive across renders without itself triggering one, and is intentionally NOT the effect's
  // only dependency (see the sync effect below).
  const highWaterRef = useRef<number>(feedBaselineTs);

  // Re-seed (raise) the high-water mark whenever the backfill baseline changes — a fresh join or
  // a mid-session reconnect. `Math.max` guards against ever LOWERING it (a defensive floor; in
  // practice `feedBaselineTs` is itself non-decreasing across welcomes).
  useEffect(() => {
    highWaterRef.current = Math.max(highWaterRef.current, feedBaselineTs);
  }, [feedBaselineTs]);

  useEffect(() => {
    const { rolls, nextHighWater } = selectFreshRolls(feed, highWaterRef.current);
    // The throw is published under the roll's OWN feed-entry id: COMMS.app holds that same card
    // until this promise resolves, so a `/roll` doesn't spoil its own dice one layer down.
    // `/roll` needs no server correlation field — the card and the dice are the same feed entry,
    // so the entry id already IS the key.
    for (const roll of rolls) registerResolutionThrow(roll.id, throwFeedRoll(roll));
    highWaterRef.current = nextHighWater;
  }, [feed]);

  return null;
}
